import { ui } from '../content/reviewedCopy.js'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { goBack } from '../lib/back'
import { useApp } from '../hooks/useApp'
import { trackEvent, Events } from '../lib/analytics'
import { upsertUser, sendMagicLink, getAllSessions } from '../lib/supabase'
import { Capacitor } from '@capacitor/core'
import { haptic } from '../lib/haptic'
import { ANNUAL_FOUNDING_PRICE, ANNUAL_FULL_PRICE, LIBRARY_TARGET, MONTHLY_PRICE, CUSTOM_AUDIO_PRICE } from '../config/pricing'
import { billingAvailable, loadPackages, purchase, restore } from '../lib/nativeBilling'
import billingCopy from '../content/billing.json'
import { apiUrl } from '../lib/apiUrl'

// Three price points, annual first. The design marks the preferred card by
// border weight only, no badge, no countdown, no struck-through price.
const PLANS = [
  {
    id: 'annual',
    label: 'Annual, founding rate',
    price: ANNUAL_FOUNDING_PRICE,
    headline: 'Includes a custom session built for you',
    note: `A$${(ANNUAL_FOUNDING_PRICE / 12).toFixed(2)} a month, billed once a year. The founding rate stays at A$${ANNUAL_FOUNDING_PRICE} for as long as you keep the subscription.`,
  },
  {
    id: 'monthly',
    label: 'Monthly',
    price: MONTHLY_PRICE,
    note: 'Billed monthly. Same content as annual.',
  },
]

export default function Premium() {
  return Capacitor.isNativePlatform() ? <NativePremium /> : <WebPremium />
}

function WebPremium() {
  const navigate = useNavigate()
  const { isPremium, userEmail, setUserEmail, addToast, refreshPremium, authUser } = useApp()
  const [selectedPlan, setSelectedPlan] = useState('annual')
  // Live count of sessions with audio, for the counter. Null until it lands,
  // and the line simply waits rather than showing a wrong number.
  const [withAudio, setWithAudio] = useState(null)
  useEffect(() => {
    getAllSessions()
      .then(rows => setWithAudio(rows.filter(s => s.has_audio).length))
      .catch(err => console.error('[Premium] getAllSessions failed:', JSON.stringify(err, Object.getOwnPropertyNames(err))))
  }, [])
  const [email, setEmail] = useState(userEmail || '')
  const [emailError, setEmailError] = useState('')
  const [loading, setLoading] = useState(false)
  const [restoring, setRestoring] = useState(false)

  const selected = PLANS.find(p => p.id === selectedPlan) || PLANS[0]

  async function handleSubscribe() {
    if (import.meta.env.MODE === 'native' || Capacitor.isNativePlatform()) return
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter the email you want the subscription on.')
      return
    }
    setEmailError('')
    haptic()
    setLoading(true)
    trackEvent(Events.PREMIUM_UPGRADE_STARTED, { plan: selectedPlan })

    try {
      setUserEmail(email)
      await upsertUser(email)

      const res = await fetch(apiUrl('/api/create-checkout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'subscription', plan: selectedPlan, email }),
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Checkout failed')
      }

      const { url, sessionId } = await res.json()
      if (url) {
        window.location.href = url
      } else {
        const { stripePromise } = await import('../lib/stripe')
        const stripe = await stripePromise
        const { error } = await stripe.redirectToCheckout({ sessionId })
        if (error) throw error
      }
    } catch (err) {
      console.error('[Premium] checkout failed:', err)
      addToast(err.message || 'Something went wrong. Please try again.', 'error')
      setLoading(false)
    }
  }

  // Re-checks the subscription. Signed in, the account decides and the typed
  // email is ignored. Signed out, a typed email unlocks nothing: it only gets
  // a sign-in link, and the app rechecks premium with the session once that
  // is tapped (useApp does this on auth change). Never writes a row.
  async function handleRestore() {
    const target = authUser?.email || email || userEmail
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      setEmailError('Enter the email you bought with, then restore.')
      return
    }
    setEmailError('')
    setRestoring(true)
    try {
      if (!authUser) {
        await sendMagicLink(target)
        addToast('Check your email for a sign-in link. Tap it to restore your purchase.', 'success', 7000)
        return
      }
      const active = await refreshPremium()
      if (active === null) return
      if (active) {
        addToast(ui.restore_active, 'success')
      } else {
        addToast('No active subscription on that email.', 'info')
      }
    } catch (err) {
      console.error('[Premium] restore failed:', err)
      addToast(authUser ? 'Could not check that email. Try again.' : 'Could not send the link. Try again.', 'error')
    } finally {
      setRestoring(false)
    }
  }

  if (isPremium) {
    return (
      <div className="page readable-page">
        <div className="status-bar"><span /><a href="/" style={{ color: 'inherit', padding: '12px 0' }} aria-label="Home">Regulated</a></div>
        <div className="page-content-wide" style={{ paddingTop: 8 }}>
          <h1 style={{ margin: '0 0 10px', font: '300 32px/38px var(--font-display)', letterSpacing: '-0.01em' }}>
            You have premium
          </h1>
          <p style={{ margin: '0 0 24px', font: '400 16px/25px var(--font-ui)', color: 'var(--ink-muted)', textWrap: 'pretty' }}>
            {ui.premium_available}
          </p>
          <button className="btn-primary btn-lg" onClick={() => navigate('/sessions')}>
            Go to the library
          </button>
          <div className="divider" />
          <CustomAudioCard onClick={() => navigate('/custom')} />
          <div className="divider" />
          <AccountBlock />
        </div>
      </div>
    )
  }

  return (
    <div className="page readable-page">
      <div className="status-bar"><span /><a href="/" style={{ color: 'inherit', padding: '12px 0' }} aria-label="Home">Regulated</a></div>

      <div style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 12px', maxWidth: 'var(--content-width, 480px)', margin: '0 auto' }}>
        <button className="btn-icon" onClick={() => goBack(navigate)} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="var(--ink-muted)" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="page-content-wide">
        <h1 style={{ margin: '0 0 10px', font: '300 32px/38px var(--font-display)', letterSpacing: '-0.01em' }}>
          Premium
        </h1>
        <p style={{ margin: '0 0 18px', font: '400 16px/25px var(--font-ui)', color: 'var(--ink-muted)', textWrap: 'pretty' }}>
          {ui.premium_intro}
        </p>
        {withAudio !== null && (
          <p className="t-caption" style={{ margin: '0 0 18px' }} data-testid="library-counter">
            {withAudio} of {LIBRARY_TARGET} sessions until the price rises to A${ANNUAL_FULL_PRICE}
          </p>
        )}

        <div className="premium-options">
          {PLANS.map(plan => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              aria-pressed={selectedPlan === plan.id}
              className={`card ${selectedPlan === plan.id ? 'card-current' : ''}`}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                boxShadow: selectedPlan === plan.id ? 'var(--e1)' : 'none',
                padding: 18,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ font: '400 21px/28px var(--font-display)', color: 'var(--ink)' }}>{plan.label}</span>
                <span style={{ font: '500 17px/24px var(--font-ui)', color: 'var(--ink)' }}>A${plan.price}</span>
              </div>
              {plan.headline && (
                <div style={{ marginTop: 6, font: '500 14px/20px var(--font-ui)', color: 'var(--ink)' }}>{plan.headline}</div>
              )}
              <div style={{ marginTop: 6, font: '400 13px/19px var(--font-ui)', color: 'var(--ink-muted)' }}>
                {plan.note}
              </div>

            </button>
          ))}

          <CustomAudioCard onClick={() => navigate('/custom')} />
        </div>

        <div className="divider" />

        <div className="form-group">
          <label className="form-label" htmlFor="premium-email">Email</label>
          <input
            id="premium-email"
            className="form-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? 'premium-email-error' : undefined}
          />
          {emailError && (
            <div id="premium-email-error" role="alert" style={{ font: '400 13px/18px var(--font-ui)', color: '#6A4B4E' }}>
              {emailError}
            </div>
          )}
        </div>

        <AccountBlock />

        <div style={{ font: '400 14px/22px var(--font-ui)', color: 'var(--ink-muted)', textWrap: 'pretty' }}>
          {ui.wellbeing_note}
        </div>
        <div style={{ height: 24 }} />
      </div>

      <div className="footer-cta" style={{ maxWidth: 'var(--content-width, 480px)', margin: '0 auto', width: '100%', background: 'transparent', borderTop: 'none', padding: '0 var(--footer-inline, 24px) 24px' }}>
        <button className="btn-primary btn-lg" onClick={handleSubscribe} disabled={loading}>
          {loading ? 'Opening checkout…' : `Continue at A$${selected.price} ${selected.id === 'annual' ? 'a year' : 'a month'}`}
        </button>
        <button className="btn-ghost" onClick={handleRestore} disabled={restoring}>
          {restoring ? 'Checking…' : 'Restore a purchase'}
        </button>
      </div>
    </div>
  )
}

function AccountBlock() {
  const navigate = useNavigate()
  const { authUser, signOut, addToast } = useApp()

  if (!authUser) {
    return (
      <div style={{ margin: '4px 0 18px' }}>
        <button className="btn-ghost" onClick={() => navigate('/signin')}>
          {ui.account_signin}
        </button>
      </div>
    )
  }

  return (
    <div style={{ margin: '4px 0 18px' }}>
      <div style={{ font: '400 14px/22px var(--font-ui)', color: 'var(--ink-muted)' }}>
        Signed in as {authUser.email}
      </div>
      <button
        className="btn-ghost"
        onClick={async () => {
          await signOut()
          addToast('Signed out.', 'info')
        }}
      >
        Sign out
      </button>
      <button className="btn-ghost" onClick={() => navigate('/delete-account')}>
        Request account deletion
      </button>
    </div>
  )
}

function CustomAudioCard({ onClick }) {
  return (
    <button onClick={onClick} className="card" style={{ textAlign: 'left', cursor: 'pointer', boxShadow: 'none', padding: 18, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ font: '400 21px/28px var(--font-display)', color: 'var(--ink)' }}>Custom audio</span>
        <span style={{ font: '500 17px/24px var(--font-ui)', color: 'var(--ink)' }}>A${CUSTOM_AUDIO_PRICE}</span>
      </div>
      <div style={{ marginTop: 6, font: '400 13px/19px var(--font-ui)', color: 'var(--ink-muted)' }}>
        One session written and recorded for your situation. Bought separately, no subscription needed.
      </div>
    </button>
  )
}

function NativePremium() {
  const { authUser, isPremium, refreshPremium } = useApp()
  const navigate = useNavigate()
  const viewRevision = useRef(0)
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  useEffect(() => {
    let live = true
    viewRevision.current++
    setBusy(false)
    setOptions([])
    setSelected('')
    setMessage('')
    if (authUser?.id) {
      loadPackages(authUser.id).then(result => {
        if (!live || result.status !== 'ready') return
        setOptions(result.packages)
        setSelected(result.packages[0].id)
      }).catch(() => { if (live) setMessage(billingCopy.unavailable) })
    }
    return () => { live = false; viewRevision.current++ }
  }, [authUser?.id])

  useEffect(() => {
    let live = true
    let listener
    import('@capacitor/app').then(({ App }) => App.addListener('appStateChange', ({ isActive }) => {
      if (live && isActive) void refreshPremium().catch(() => {})
    })).then(handle => { if (live) listener = handle; else void handle.remove() })
    return () => { live = false; void listener?.remove() }
  }, [refreshPremium])

  async function complete(restoring) {
    if (busy || !authUser?.id) return
    const captured = viewRevision.current
    const current = () => captured === viewRevision.current
    setBusy(true)
    setMessage(billingCopy.verifying)
    try {
      const result = restoring ? await restore(authUser.id) : await purchase(authUser.id, selected)
      if (!current()) return
      if (result.status === 'stale' || result.status === 'canceled') { setMessage(''); return }
      if (result.status === 'pending') { setMessage(billingCopy.paymentPending); return }
      const active = await refreshPremium()
      if (!current()) return
      if (active === null) { setMessage(''); return }
      setMessage(active ? (restoring ? ui.restore_active : ui.purchase_active) : billingCopy.verificationPending)
    } catch {
      if (!current()) return
      setMessage(restoring ? billingCopy.restoreError : billingCopy.purchaseError)
    } finally { if (current()) setBusy(false) }
  }

  return (
    <div className="page readable-page native-premium-page">
      <div className="status-bar"><span /><a href="/" style={{ color: 'inherit', padding: '12px 0' }} aria-label="Home">Regulated</a></div>
      <div className="page-content-wide">
        <h1>{isPremium ? 'You have premium' : 'Premium'}</h1>
        {!authUser ? <button className="btn-primary btn-lg" onClick={() => navigate('/signin')}>{billingCopy.signIn}</button> : <>
          {!isPremium && <>
            <div className="premium-options">{options.map(option => (
              <button key={option.id} className={`card ${selected === option.id ? 'card-current' : ''}`} disabled={busy} aria-pressed={selected === option.id} onClick={() => setSelected(option.id)}>
                <span>{option.title}</span> <span>{option.price} {option.period === 'P1M' ? 'a month' : 'a year'}</span>
              </button>
            ))}</div>
            <button className="btn-primary btn-lg" disabled={busy || !options.length} onClick={() => complete(false)}>{billingCopy.subscribe}</button>
            {options.length > 0 && <p>{billingCopy.recurring}</p>}
          </>}
          {isPremium && <button className="btn-primary btn-lg" onClick={() => navigate('/sessions')}>Go to the library</button>}
          <button className="btn-ghost" disabled={busy || !billingAvailable()} onClick={() => complete(true)}>Restore a purchase</button>
          <a className="btn-ghost" href={Capacitor.getPlatform() === 'ios' ? 'https://apps.apple.com/account/subscriptions' : 'https://play.google.com/store/account/subscriptions'}>{billingCopy.manage}</a>
        </>}
        <p role="status">{message}</p>
        <AccountBlock />
      </div>
    </div>
  )
}
